/**
 * Fixed-size ring buffer + a `console.error`/`console.warn` capture hook.
 * Used to attach the last N console errors/warnings to a bug report so
 * reporters don't have to manually copy-paste from devtools.
 */

export interface RingBuffer<T> {
  push(item: T): void;
  /** Returns a snapshot array; mutating it never affects the buffer. */
  toArray(): T[];
}

export function createRingBuffer<T>(max = 20): RingBuffer<T> {
  let items: T[] = [];

  return {
    push(item: T) {
      items.push(item);
      if (items.length > max) {
        items = items.slice(items.length - max);
      }
    },
    toArray() {
      return items.slice();
    },
  };
}

export interface ConsoleErrorEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: string;
}

export interface InstallConsoleCaptureOptions {
  /** The console object to wrap. Defaults to the global `console`. */
  console?: Console;
}

function stringifyArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg === undefined) return 'undefined';
  if (arg === null) return 'null';
  if (arg instanceof Error) return arg.stack ?? arg.message;

  try {
    const json = JSON.stringify(arg);
    return json === undefined ? String(arg) : json;
  } catch {
    return String(arg);
  }
}

interface InstalledState {
  originalError: Console['error'];
  originalWarn: Console['warn'];
  refCount: number;
}

// Tracks which console objects are currently wrapped, so calling
// `installConsoleCapture` more than once on the same console (e.g. from
// multiple widget instances) wraps `error`/`warn` exactly once and only
// restores the originals once every installer has uninstalled.
const installedConsoles = new WeakMap<Console, InstalledState>();

export function installConsoleCapture(
  buffer: RingBuffer<ConsoleErrorEntry>,
  opts: InstallConsoleCaptureOptions = {},
): () => void {
  const targetConsole = opts.console ?? console;

  let state = installedConsoles.get(targetConsole);
  if (!state) {
    const originalError = targetConsole.error;
    const originalWarn = targetConsole.warn;

    const capture = (level: ConsoleErrorEntry['level'], original: (...args: unknown[]) => void) =>
      (...args: unknown[]) => {
        buffer.push({
          level,
          message: args.map(stringifyArg).join(' '),
          timestamp: new Date().toISOString(),
        });
        original.apply(targetConsole, args);
      };

    targetConsole.error = capture('error', originalError.bind(targetConsole)) as Console['error'];
    targetConsole.warn = capture('warn', originalWarn.bind(targetConsole)) as Console['warn'];

    state = { originalError, originalWarn, refCount: 0 };
    installedConsoles.set(targetConsole, state);
  }

  state.refCount += 1;
  let uninstalled = false;

  return () => {
    if (uninstalled) return;
    uninstalled = true;

    const current = installedConsoles.get(targetConsole);
    if (!current) return;

    current.refCount -= 1;
    if (current.refCount <= 0) {
      targetConsole.error = current.originalError;
      targetConsole.warn = current.originalWarn;
      installedConsoles.delete(targetConsole);
    }
  };
}
